import React from 'react';
import { Box, Alert } from '@mui/material';
import MarkdownRenderer from './MarkdownRenderer';
import ScormPlayer from './ScormPlayer';
import VideoPlayer from './VideoPlayer';
import InteractiveSteps from './InteractiveSteps';
import ApiDocViewer from './ApiDocViewer';

interface ContentRendererProps {
  contentType: string;
  contentUrl?: string | null;
  contentText?: string | null;
  contentJson?: any;
  lessonId: string;
  lessonSlug: string;
  courseSlug: string;
  assetBaseUrl?: string;
  onComplete?: (data: any) => void;
  onProgress?: (data: any) => void;
  onNavigateNext?: () => void;
}

export default function ContentRenderer({
  contentType,
  contentUrl,
  contentText,
  contentJson,
  lessonId,
  lessonSlug,
  courseSlug,
  assetBaseUrl,
  onComplete,
  onProgress,
  onNavigateNext,
}: ContentRendererProps) {
  // Normalize content type
  const type = contentType.toLowerCase();

  // SCORM content
  if (type === 'scorm' || type === 'scorm_package') {
    if (!contentUrl) {
      return (
        <Box display="flex" justifyContent="center" alignItems="center" height="100%">
          <Alert severity="warning">Kein SCORM-Paket verfuegbar.</Alert>
        </Box>
      );
    }

    // Render SCORM player - fills available height
    return (
      <Box sx={{ width: '100%', flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        <ScormPlayer
          contentUrl={contentUrl}
          lessonId={lessonId}
          lessonSlug={lessonSlug}
          courseSlug={courseSlug}
          onComplete={onComplete}
          onProgress={onProgress}
          onNavigateNext={onNavigateNext}
        />
      </Box>
    );
  }

  // cmi5 content (xAPI-based e-learning standard)
  if (type === 'cmi5') {
    if (!contentUrl) {
      return (
        <Box display="flex" justifyContent="center" alignItems="center" height="100%">
          <Alert severity="warning">Kein cmi5-Paket verfuegbar.</Alert>
        </Box>
      );
    }

    // cmi5 content is launched via iframe with xAPI parameters in the URL
    // The backend provides the full launch URL with endpoint, fetch, actor, registration, activityId
    return (
      <Box sx={{ width: '100%', height: '100%', minHeight: 'calc(100vh - 200px)', flex: 1 }}>
        <iframe
          src={contentUrl}
          style={{
            width: '100%',
            height: '100%',
            border: 'none',
            minHeight: 'calc(100vh - 200px)',
          }}
          title="cmi5 Content"
          allow="fullscreen"
          sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-modals"
        />
      </Box>
    );
  }

  // Markdown content
  if (type === 'markdown' || type === 'text' || type === 'article') {
    const content = contentText || '';

    if (!content) {
      return (
        <Box display="flex" justifyContent="center" alignItems="center" height="100%">
          <Alert severity="info">Kein Textinhalt verfuegbar.</Alert>
        </Box>
      );
    }

    return (
      <Box sx={{ p: 3, maxWidth: 900, mx: 'auto' }}>
        <MarkdownRenderer content={content} assetBaseUrl={assetBaseUrl} />
      </Box>
    );
  }

  // PDF content
  if (type === 'pdf') {
    if (!contentUrl) {
      return (
        <Box display="flex" justifyContent="center" alignItems="center" height="100%">
          <Alert severity="warning">Kein PDF verfuegbar.</Alert>
        </Box>
      );
    }

    return (
      <Box sx={{ width: '100%', height: '100%', minHeight: 'calc(100vh - 200px)' }}>
        <iframe
          src={contentUrl}
          style={{
            width: '100%',
            height: '100%',
            border: 'none',
          }}
          title="PDF Viewer"
        />
      </Box>
    );
  }

  // Video content
  if (type === 'video') {
    if (!contentUrl) {
      return (
        <Box display="flex" justifyContent="center" alignItems="center" height="100%">
          <Alert severity="warning">Kein Video verfuegbar.</Alert>
        </Box>
      );
    }

    return (
      <VideoPlayer
        src={contentUrl}
        onProgress={(data) => {
          onProgress?.({ type: 'video', ...data });
        }}
        onComplete={() => {
          onComplete?.({ type: 'video', completed: true });
        }}
      />
    );
  }

  // External link / Weblink
  if (type === 'weblink' || type === 'external' || type === 'iframe') {
    if (!contentUrl) {
      return (
        <Box display="flex" justifyContent="center" alignItems="center" height="100%">
          <Alert severity="warning">Kein Link verfuegbar.</Alert>
        </Box>
      );
    }

    return (
      <Box sx={{ width: '100%', height: '100%', minHeight: 'calc(100vh - 200px)' }}>
        <iframe
          src={contentUrl}
          style={{
            width: '100%',
            height: '100%',
            border: 'none',
          }}
          title="External Content"
          sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
        />
      </Box>
    );
  }

  // Interactive step-by-step content
  if (type === 'interactive' || type === 'step-by-step' || type === 'walkthrough') {
    // Content can be passed as contentJson or parsed from contentText
    let interactiveContent = contentJson;

    if (!interactiveContent && contentText) {
      try {
        interactiveContent = JSON.parse(contentText);
      } catch (e) {
        console.error('Failed to parse interactive content JSON:', e);
      }
    }

    if (!interactiveContent?.steps || interactiveContent.steps.length === 0) {
      return (
        <Box display="flex" justifyContent="center" alignItems="center" height="100%">
          <Alert severity="warning">Keine interaktiven Schritte verfuegbar.</Alert>
        </Box>
      );
    }

    return (
      <InteractiveSteps
        content={interactiveContent}
        assetBaseUrl={assetBaseUrl}
        onProgress={(data) => {
          onProgress?.({ type: 'interactive', ...data });
        }}
        onComplete={() => {
          onComplete?.({ type: 'interactive', completed: true });
        }}
      />
    );
  }

  // API Documentation content
  if (type === 'api-doc' || type === 'api_doc' || type === 'openapi' || type === 'swagger') {
    // Content can be passed as contentJson or parsed from contentText
    let apiContent = contentJson;

    if (!apiContent && contentText) {
      try {
        apiContent = JSON.parse(contentText);
      } catch (e) {
        console.error('Failed to parse API doc content JSON:', e);
      }
    }

    if (!apiContent) {
      return (
        <Box display="flex" justifyContent="center" alignItems="center" height="100%">
          <Alert severity="warning">Keine API-Dokumentation verfuegbar.</Alert>
        </Box>
      );
    }

    return (
      <ApiDocViewer
        content={apiContent}
        onComplete={() => {
          onComplete?.({ type: 'api-doc', completed: true });
        }}
      />
    );
  }

  // Unknown content type
  return (
    <Box display="flex" justifyContent="center" alignItems="center" height="100%">
      <Alert severity="error">
        Unbekannter Inhaltstyp: {contentType}
      </Alert>
    </Box>
  );
}
